export type AppRouteId =
  | 'login'
  | 'login-verify'
  | 'password-reset'
  | 'signup-password'
  | 'event-list'
  | 'device-management'
  | 'notice-management'
  | 'b2b-inquiry-management'
  | 'qna-management'
  | 'certificate-template'
  | 'homepage-user-management'
  | 'goods-representative-image'
  | 'user-management'
  | 'event-info'
  | 'timing-points'
  | 'categories'
  | 'splits'
  | 'participants'
  | 'event-reader-devices'
  | 'statistics-summary'
  | 'statistics-split'
  | 'statistics-live-position'
  | 'statistics-cumulative'
  | 'leaderboard'
  | 'results'
  | 'raw-data';

export type AppRoute = {
  id: AppRouteId;
  label: string;
  path: string;
  section: 'auth' | 'admin' | 'competition' | 'statistics' | 'results';
  /** true면 대회 진입 전 전역 관리 화면(헤더 + 전역 사이드바)로 렌더 */
  standalone?: boolean;
};

export const LOGIN_ROUTE_PATH = '/login';
export const LOGIN_VERIFY_ROUTE_PATH = '/login/verify';
export const PASSWORD_RESET_ROUTE_PATH = '/password-reset';
export const SIGNUP_PASSWORD_ROUTE_PATH = '/signup-password';
export const EVENT_LIST_ROUTE_PATH = '/events';
export const DEFAULT_ROUTE_PATH = EVENT_LIST_ROUTE_PATH;
export const EVENT_ID_SEARCH_PARAM = 'eventId';

export function getEventIdFromSearch(search: string) {
  const rawEventId = new URLSearchParams(search).get(EVENT_ID_SEARCH_PARAM);
  if (rawEventId == null) return null;

  const eventId = Number(rawEventId);
  return Number.isInteger(eventId) && eventId > 0 ? eventId : null;
}

export function buildEventScopedPath(path: string, eventId: number | null) {
  if (eventId == null) return path;

  const [pathname, search = ''] = path.split('?');
  const searchParams = new URLSearchParams(search);
  searchParams.set(EVENT_ID_SEARCH_PARAM, String(eventId));

  return `${pathname}?${searchParams.toString()}`;
}

export const APP_ROUTES: AppRoute[] = [
  { id: 'login', label: '로그인', path: LOGIN_ROUTE_PATH, section: 'auth' },
  { id: 'login-verify', label: '이메일 인증', path: LOGIN_VERIFY_ROUTE_PATH, section: 'auth' },
  { id: 'password-reset', label: '비밀번호 재설정', path: PASSWORD_RESET_ROUTE_PATH, section: 'auth' },
  { id: 'signup-password', label: '비밀번호 설정', path: SIGNUP_PASSWORD_ROUTE_PATH, section: 'auth' },
  {
    id: 'event-list',
    label: '대회 목록',
    path: EVENT_LIST_ROUTE_PATH,
    section: 'admin',
    standalone: true,
  },
  {
    id: 'device-management',
    label: '기기 관리',
    path: '/devices',
    section: 'admin',
    standalone: true,
  },
  {
    id: 'notice-management',
    label: '공지사항 관리',
    path: '/notices',
    section: 'admin',
    standalone: true,
  },
  {
    id: 'b2b-inquiry-management',
    label: 'B2B 문의 관리',
    path: '/homepage/b2b-inquiries',
    section: 'admin',
    standalone: true,
  },
  {
    id: 'qna-management',
    label: 'QnA 관리',
    path: '/homepage/qna',
    section: 'admin',
    standalone: true,
  },
  {
    id: 'certificate-template',
    label: '완주증 템플릿 등록',
    path: '/homepage/certificate-template',
    section: 'admin',
    standalone: true,
  },
  {
    id: 'homepage-user-management',
    label: '홈페이지 사용자 관리',
    path: '/homepage/users',
    section: 'admin',
    standalone: true,
  },
  {
    id: 'goods-representative-image',
    label: '굿즈 이미지 관리',
    path: '/homepage/goods-representative',
    section: 'admin',
    standalone: true,
  },
  {
    id: 'user-management',
    label: '사용자 관리',
    path: '/users',
    section: 'admin',
    standalone: true,
  },
  { id: 'event-info', label: '대회 정보', path: '/events/info', section: 'competition' },
  {
    id: 'timing-points',
    label: '계측 지점 관리',
    path: '/events/timing-points',
    section: 'competition',
  },
  { id: 'categories', label: '종목 관리', path: '/events/categories', section: 'competition' },
  { id: 'splits', label: '구간 관리', path: '/events/splits', section: 'competition' },
  { id: 'participants', label: '참가자 관리', path: '/events/participants', section: 'competition' },
  {
    id: 'event-reader-devices',
    label: '대회 리더기 설정',
    path: '/events/reader-devices',
    section: 'competition',
  },
  {
    id: 'statistics-summary',
    label: '참가자 연령 통계',
    path: '/statistics/basic',
    section: 'statistics',
  },
  {
    id: 'statistics-split',
    label: '참가자 별 구간 통계',
    path: '/statistics/section-distribution',
    section: 'statistics',
  },
  {
    id: 'statistics-live-position',
    label: '실시간 참가자 위치 현황',
    path: '/statistics/live-position',
    section: 'statistics',
  },
  {
    id: 'statistics-cumulative',
    label: '누적 참가자 구간 통과 통계',
    path: '/statistics/cumulative-analysis',
    section: 'statistics',
  },
  { id: 'leaderboard', label: '리더보드', path: '/statistics/leaderboard', section: 'statistics' },
  { id: 'results', label: '결과 조회', path: '/results', section: 'results' },
  { id: 'raw-data', label: '원시 데이터 조회', path: '/results/raw-data', section: 'results' },
];

const ROUTE_ALIASES: Record<string, string> = {
  '/statistics/gender': '/statistics/cumulative-analysis',
  '/statistics/gender-analysis': '/statistics/cumulative-analysis',
};

export function getRouteByPath(path: string) {
  const resolvedPath = ROUTE_ALIASES[path] ?? path;

  return APP_ROUTES.find((route) => route.path === resolvedPath);
}

export function getDefaultRoute() {
  return getRouteByPath(DEFAULT_ROUTE_PATH) ?? APP_ROUTES[0];
}
